import { type IDialogManagerButton, type IDialogManagerOptions, type IPopupManagerInstance } from '../../popup';
import { PageAction } from './page-action';
import { type IDialogManagerButtonTooltip } from '../../popup/services/managers/dialog/dialog-manager.service';

export interface IPageDialogActionComponentApi<Response extends Promise<any>> {
    isValid?(): boolean;
    submit?(): Response;
    getTooltip?(): IDialogManagerButtonTooltip;
}

// export const CANCEL_SUBMIT = 'CANCEL SUBMIT'; unused

export abstract class PageDialogAction<
    RequestData = any,
    API extends IPageDialogActionComponentApi<Promise<ReturnType<API['submit']>>> = any
> extends PageAction<RequestData, ReturnType<API['submit']>> {
    protected componentApi: API;
    protected requestData: RequestData;
    protected dialog: IPopupManagerInstance<ReturnType<API['submit']>>;
    protected dialogParentSelector?: string;
    protected dialogWindowClass: string = '';

    constructor(
        protected $injector: angular.auto.IInjectorService,
        protected $scope: any,
        protected scopeStateFieldName: string = 'detailsState'
    ) {
        super($injector, $scope);
    }

    public run(request: RequestData = null): ReturnType<API['submit']> {
        this.componentApi = null;
        this.requestData = request;

        const scope = this.$scope.$new();
        scope.onComponentReady = this.setComponentApi.bind(this);
        this.initScope(scope);
        const options: IDialogManagerOptions = {
            scope: scope,
            template: this.dialogTemplate,
            titleTemplate: this.dialogTitleTemplate,
            scrollbar: this.isDialogScrollable,
            title: this.dialogTitle,
            variables: this.dialogTitleVariables,
            classes: this.dialogClasses,
            buttons: this.buttons,
            windowClass: this.dialogWindowClass,
            footer: this.hasFooter,
            closeEnabled: this.isCloseEnabled
        };
        if (this.dialogParentSelector) {
            options.selector = this.dialogParentSelector;
        }
        this.dialog = this.dialogManager.dialog(options);
        return this.dialog.result
            .then(result => {
                scope.$destroy();
                return result;
            })
            .catch(error => {
                scope.$destroy();
                throw error;
            }) as ReturnType<API['submit']>;
    }

    protected initScope(scope: any) {
        for (const key in this.requestData)
            if (Object.prototype.hasOwnProperty.call(this.requestData, key)) {
                scope[key] = this.requestData[key];
            }
    }

    protected abstract get dialogTemplate(): string;

    protected abstract get dialogTitle(): string;

    protected get dialogTitleVariables() {
        return null;
    }

    protected get dialogTitleTemplate() {
        return null;
    }

    protected get dialogClasses(): string {
        return '';
    }

    protected get isDialogScrollable() {
        return true;
    }

    protected abstract get buttons(): IDialogManagerButton[];

    protected get cancelButton() {
        return {
            title: 'common.actions.cancel',
            classes: 'cancel',
            action: function (popupScope) {
                popupScope.$dismiss();
            },
            disabled: () => this.isCancelButtonDisabled
        };
    }

    protected setComponentApi(api: API) {
        this.componentApi = api;
    }

    protected get isCancelButtonDisabled() {
        return false;
    }

    protected get formApi() {
        return this.componentApi;
    }

    protected get hasFooter(): boolean {
        return true;
    }

    protected get isCloseEnabled(): boolean {
        return true;
    }
}
